import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { removeBuildingOwnBlueprintUnderlays } from '@shared/helpers/design/blueprint-underlay.helpers';
import { Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IconButton } from '@ui/buttons';
import { SidePanel } from '@ui/layout';
import {
  useAddEmptyBuilding,
  useAddStorey,
  useSelectedStorey,
  useStoreyGfaRecord,
  useMaxStoreyGfa,
  useIsAddStoreyAllowed,
  useProjectDesignGfa,
  usePanelStoreys,
} from '../hooks/storeys.hooks';
import {
  useBuildingCandidate,
  useHasVisibleBuildingSelection,
  useIsBuildingOutlineSelected,
  useRequestPropertiesPanel,
  useSelectBuildingOutline,
  useSelectedBuildingId,
  useSetBuildingCandidate,
  useSetSelectedStoreyId,
} from '../store/design-store.hooks';
import { DesignAreaField } from '../components/DesignAreaField';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import {
  getBuildingDesign,
  useBuildingDesign,
  useUpdateBuildingDesign,
} from '../hooks/design.hooks';
import {
  classifyDesignPersistSettlement,
  ignoreDesignSnapshotSuperseded,
} from '../helpers/design-snapshot-persist.helpers';
import { BuildingList } from './BuildingList';
import { GhostButton } from '@ui/GhostButton';
import { Icon } from '@ui/Icon';
import { canAddBuilding } from '@shared/helpers/design/building.helpers';
import {
  getBuildingDisplayName,
  getDefaultBuildingName,
} from '@shared/helpers/design/building.helpers';
import {
  useAdoptSingleBuildingSelection,
  useCancelEmptyBuildingOnEscape,
  useDeselectBuilding,
  useResetBuildingSelection,
} from '../hooks/storeys.hooks';
import { StoreyRow } from './StoreyRow';
import { AddActivityRefMenuProvider } from './activity-reference-bar/AddActivityRefMenuContext';
import { AddActivityRefMenu } from './activity-reference-bar/AddActivityRefMenu';
import { BuildingGfaInput } from './BuildingGfaInput';
import { BuildingStoreyCountInput } from './BuildingStoreyCountInput';
import { BuildingFootprintTemplateList } from './BuildingFootprintTemplateList';
import { BuildingNameInput } from './BuildingNameInput';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { PANEL_CONTENT_DEFAULT_SPACING } from '@ui/layout/panels/panel.constants';
import { useStoreyKeyboardShortcuts } from './use-storey-keyboard-shortcuts.hook';
import {
  getBuildingById,
  isEmptyStoreysGeometry,
} from '@shared/helpers/design/storey.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { shouldIgnoreKeyboardShortcut } from '@shared/helpers/event.helpers';
import { useSnackbarWithUndo } from '@client/hooks/use-undo-snackbar.hooks';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { suppressNextDesignMapLocationFlyTo } from '../store/design.store';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  selectedBuildingTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  gfaColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  buildingsHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(PANEL_CONTENT_DEFAULT_SPACING),
    paddingBottom: theme.spacing(PANEL_CONTENT_DEFAULT_SPACING),
  },
  newBuildingRow: {
    paddingLeft: 4,
  },
  newBuildingFocus: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    columnGap: theme.spacing(0.5),
    rowGap: theme.spacing(3),
    paddingTop: theme.spacing(PANEL_CONTENT_DEFAULT_SPACING),
    paddingBottom: theme.spacing(PANEL_CONTENT_DEFAULT_SPACING),
  },
  newBuildingHeader: {
    display: 'contents',
  },
  newBuildingName: {
    gridColumn: 2,
    minWidth: 0,
    // Center the label against the 28 px back button without moving the button.
    paddingTop: 7,
    // Match the right edge of the GFA control below.
    paddingRight: `calc(28px + ${theme.spacing(0.5)})`,
    '& > :last-child': {
      width: '100%',
    },
  },
  newBuildingMetrics: {
    gridColumn: 2,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    // Mirror the back-button column and grid gap on the right-hand side.
    paddingRight: `calc(28px + ${theme.spacing(0.5)})`,
  },
  newBuildingFootprints: {
    gridColumn: '1 / -1',
  },
  storeyCount: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  list: {
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    listStyle: 'none',
  },
}));

export const Storeys: React.FC = () => {
  const { classes } = useStyles();
  const panelStoreys = usePanelStoreys();
  const selectedStorey = useSelectedStorey();
  const isBuildingOutlineSelected = useIsBuildingOutlineSelected();
  const hasVisibleBuildingSelection = useHasVisibleBuildingSelection();
  const { buildings } = useBuildingDesign();
  const projectGfa = useProjectDesignGfa();
  const selectedBuildingId = useSelectedBuildingId();
  const deselectBuilding = useDeselectBuilding();
  const resetBuildingSelection = useResetBuildingSelection();
  useCancelEmptyBuildingOnEscape();
  const adoptSingleBuildingSelection = useAdoptSingleBuildingSelection();
  const enqueueSnackbarWithUndo = useSnackbarWithUndo();
  const { enqueueSnackbar } = useSnackbar();
  const selectBuildingOutline = useSelectBuildingOutline();
  const requestPropertiesPanel = useRequestPropertiesPanel();
  // Stale ids must not fall back to buildings[0] — same rule as panel scope.
  const selectedBuildingIndex = buildings.findIndex(
    (building) => building.id === selectedBuildingId,
  );
  const selectedBuilding =
    selectedBuildingIndex >= 0 ? buildings[selectedBuildingIndex] : undefined;
  const buildingCandidate = useBuildingCandidate();
  const setBuildingCandidate = useSetBuildingCandidate();
  const addEmptyBuilding = useAddEmptyBuilding();
  const updateBuildingDesign = useUpdateBuildingDesign();
  const [isEditingBuildingName, setIsEditingBuildingName] = useState(false);
  const [isEditingNewBuildingName, setIsEditingNewBuildingName] =
    useState(false);
  const [newBuildingName, setNewBuildingName] = useState('');

  const handleAddBuilding = useCallback(() => {
    void addEmptyBuilding();
  }, [addEmptyBuilding]);
  const setSelectedStoreyId = useSetSelectedStoreyId();
  /** Only storeys explicitly opened by user are false; missing/undefined means collapsed. */
  const [collapsedSettings, setCollapsedSettings] = useState<
    Record<string, boolean>
  >({});
  const addStorey = useAddStorey();
  const storeyGfaRecord = useStoreyGfaRecord();
  const maxStoreyGfa = useMaxStoreyGfa();
  const isAddStoreyAllowed = useIsAddStoreyAllowed();
  const isReadonly = useIsReadonly();
  const canAddStorey = isAddStoreyAllowed && !isReadonly;
  const canAddNewBuilding = !isReadonly && canAddBuilding(buildings);
  const addStoreyTooltip = isReadonly
    ? 'Read-only mode'
    : isAddStoreyAllowed
      ? ''
      : 'Add at least one shape to the top storey before adding a new storey';
  const selectedBuildingName = selectedBuilding
    ? getBuildingDisplayName(selectedBuilding, selectedBuildingIndex)
    : 'Building';
  const newBuildingPlaceholderName = getDefaultBuildingName(
    Math.max(0, selectedBuildingIndex),
  );

  useEffect(() => {
    const candidateName = buildingCandidate?.name?.trim();
    setNewBuildingName(
      candidateName && candidateName !== newBuildingPlaceholderName
        ? candidateName
        : '',
    );
  }, [
    buildingCandidate?.id,
    buildingCandidate?.name,
    newBuildingPlaceholderName,
  ]);

  const handleSaveNewBuildingName = useCallback(
    (name: string) => {
      setIsEditingNewBuildingName(false);
      if (!buildingCandidate) return;
      const nextName = name.trim();
      setNewBuildingName(nextName);
      setBuildingCandidate({
        ...buildingCandidate,
        name: nextName || newBuildingPlaceholderName,
      });
    },
    [buildingCandidate, newBuildingPlaceholderName, setBuildingCandidate],
  );

  const handleEditNewBuildingName = useCallback(() => {
    if (!isReadonly) setIsEditingNewBuildingName(true);
  }, [isReadonly]);

  const handleCancelNewBuildingNameEdit = useCallback(() => {
    setIsEditingNewBuildingName(false);
  }, []);

  const handleEditBuildingName = useCallback(() => {
    if (!isReadonly) setIsEditingBuildingName(true);
  }, [isReadonly]);

  const handleCancelBuildingNameEdit = useCallback(() => {
    setIsEditingBuildingName(false);
  }, []);

  const handleSaveBuildingName = useCallback(
    (name: string) => {
      if (!selectedBuilding) return;
      const defaultName = getDefaultBuildingName(selectedBuildingIndex);
      const nextName = name.trim() || defaultName;
      const currentName = selectedBuilding.name?.trim() || defaultName;
      if (nextName === currentName) {
        setIsEditingBuildingName(false);
        return;
      }
      void ignoreDesignSnapshotSuperseded(
        updateBuildingDesign((design) => ({
          ...design,
          buildings: design.buildings.map((building) =>
            building.id === selectedBuilding.id
              ? { ...building, name: nextName }
              : building,
          ),
        })),
      ).finally(() => setIsEditingBuildingName(false));
    },
    [selectedBuilding, selectedBuildingIndex, updateBuildingDesign],
  );

  const hasSingleBuilding = buildings.length === 1;
  const orderedStoreys = useMemo(
    () => panelStoreys.toReversed(),
    [panelStoreys],
  );
  const totalStoreys = orderedStoreys.length;
  const hasCompletedStorey = !isEmptyStoreysGeometry(panelStoreys);
  // A completed single-building project always uses its storey view. The
  // overview remains useful for an unfinished candidate, and for projects
  // where there are multiple buildings to choose from.
  const showBuildingOverview =
    !hasSingleBuilding || !hasCompletedStorey
      ? !hasVisibleBuildingSelection || isBuildingOutlineSelected
      : false;
  const showSingleBuildingView =
    hasCompletedStorey &&
    (hasSingleBuilding ||
      (hasVisibleBuildingSelection && !isBuildingOutlineSelected));
  const showNewBuildingFocus =
    hasVisibleBuildingSelection &&
    !isBuildingOutlineSelected &&
    !hasCompletedStorey;

  useEffect(() => {
    setIsEditingNewBuildingName(showNewBuildingFocus);
  }, [buildingCandidate?.id, showNewBuildingFocus]);

  /**
   * Sets the selected storey and ensures its settings panel is visible.
   * @param storeyId
   */
  const handleSelectStorey = useCallback(
    (storeyId: string) => {
      // Picking a storey implies its building, which may have been deselected.
      adoptSingleBuildingSelection();
      setSelectedStoreyId(storeyId);
      requestPropertiesPanel();
      // Keep the settings panel open when the row is selected.
      setCollapsedSettings((prev) => ({ ...prev, [storeyId]: false }));
    },
    [adoptSingleBuildingSelection, requestPropertiesPanel, setSelectedStoreyId],
  );

  /**
   * Toggles the storey settings panel without altering other rows.
   * @param storeyId
   */
  const handleToggleStoreySettings = useCallback(
    (storeyId: string) => {
      const selectedStoreyId = selectedStorey?.id;
      adoptSingleBuildingSelection();
      setSelectedStoreyId(storeyId);
      requestPropertiesPanel();
      setCollapsedSettings((prev) => {
        const isCollapsed = prev[storeyId] ?? false;
        const shouldCollapse =
          storeyId === selectedStoreyId ? !isCollapsed : false;
        // Toggle only when already selected; otherwise force open.
        return { ...prev, [storeyId]: shouldCollapse };
      });
    },
    [
      adoptSingleBuildingSelection,
      requestPropertiesPanel,
      selectedStorey?.id,
      setSelectedStoreyId,
    ],
  );

  const expandSettingsForStorey = useCallback((storeyId: string) => {
    setCollapsedSettings((prev) => ({ ...prev, [storeyId]: false }));
  }, []);

  useStoreyKeyboardShortcuts({
    expandSettingsForStorey,
    enabled: hasCompletedStorey,
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        !isBuildingOutlineSelected ||
        !selectedBuilding ||
        shouldIgnoreKeyboardShortcut(event) ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.repeat ||
        (event.key !== 'Backspace' && event.key !== 'Delete')
      ) {
        return;
      }

      event.preventDefault();
      const deletedBuildingId = selectedBuilding.id;
      const isLastBuilding = buildings.length === 1;
      // Clear the selection before the update. Left in place it would be healed
      // onto a neighbouring building first, briefly marking one of its storeys.
      deselectBuilding();
      const settleDelete = (
        outcome:
          | { status: 'fulfilled'; value: unknown }
          | { status: 'rejected'; reason: unknown },
      ): void => {
        const settlement = classifyDesignPersistSettlement(outcome);
        switch (settlement) {
          case 'saved':
            enqueueSnackbarWithUndo('Building deleted', {
              variant: 'success',
              withUndo: true,
            });
            return;
          case 'cancelled':
            // A newer edit won. Restore the outline only while this building remains.
            if (!getBuildingById(getBuildingDesign(), deletedBuildingId))
              return;
            selectBuildingOutline(deletedBuildingId);
            return;
          case 'failed':
            enqueueSnackbar('Could not delete the building', {
              variant: 'error',
            });
            // The building is still there, so hand its selection back.
            selectBuildingOutline(deletedBuildingId);
            return;
          default:
            return assertUnreachable(settlement);
        }
      };
      void updateBuildingDesign(
        (design) => ({
          ...removeBuildingOwnBlueprintUnderlays(design, deletedBuildingId),
          buildings: design.buildings.filter(
            (building) => building.id !== deletedBuildingId,
          ),
        }),
        {
          onOptimisticLocalUpdate: () => {
            // Normalization rebases the design location after deleting a
            // building. The remaining geometry does not move, so neither
            // should the camera.
            suppressNextDesignMapLocationFlyTo();
            // Removing the last building re-seeds one empty building
            // (`ensureDesignHasBuilding`). Return to the state a new project
            // starts in, the moment that design lands.
            if (isLastBuilding) resetBuildingSelection();
          },
          events: (design) => {
            const index = design.buildings.findIndex(
              ({ id }) => id === deletedBuildingId,
            );
            const building = design.buildings[index];
            if (!building || isEmptyStoreysGeometry(building.storeys)) {
              return [];
            }
            return [
              {
                kind: 'building-deleted',
                buildingId: deletedBuildingId,
                name: getBuildingDisplayName(building, index),
              },
            ];
          },
        },
      ).then(
        (result) => settleDelete({ status: 'fulfilled', value: result }),
        (error: unknown) => settleDelete({ status: 'rejected', reason: error }),
      );
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    buildings,
    deselectBuilding,
    enqueueSnackbar,
    enqueueSnackbarWithUndo,
    isBuildingOutlineSelected,
    resetBuildingSelection,
    selectBuildingOutline,
    selectedBuilding,
    updateBuildingDesign,
  ]);

  return (
    <AddActivityRefMenuProvider>
      <SidePanel.Content.Section>
        {showBuildingOverview && buildings.length > 0 && (
          <>
            {/* Own row instead of Section.Header: the button stays top
                aligned next to the two-line GFA block. */}
            <div className={classes.buildingsHeader}>
              <div className={classes.newBuildingRow}>
                <GhostButton
                  startIcon={<Icon category="mui" icon="Add" size="small" />}
                  onClick={handleAddBuilding}
                  disabled={!canAddNewBuilding}
                >
                  New building
                </GhostButton>
              </div>
              <div className={classes.gfaColumn}>
                <DesignAreaField
                  label="Gross Floor Area"
                  value={getDisplayNumber(Math.round(projectGfa))}
                  variant="h4"
                  readonly
                />
              </div>
            </div>
            <div className={classes.root}>
              <BuildingList />
            </div>
          </>
        )}
        {showNewBuildingFocus && (
          <div className={classes.newBuildingFocus}>
            <div className={classes.newBuildingHeader}>
              <Tooltip title="Back">
                <IconButton
                  category="mui"
                  icon="KeyboardArrowLeft"
                  shape="square"
                  sx={{ alignSelf: 'start' }}
                  onClick={deselectBuilding}
                  aria-label="Show all buildings"
                />
              </Tooltip>
              <div className={classes.newBuildingName}>
                <BuildingNameInput
                  label="New building name"
                  value={newBuildingName}
                  defaultValue={newBuildingPlaceholderName}
                  variant="h4"
                  editing={isEditingNewBuildingName}
                  fullWidth
                  onClick={handleEditNewBuildingName}
                  onCancel={handleCancelNewBuildingNameEdit}
                  onSave={handleSaveNewBuildingName}
                />
              </div>
            </div>
            <div className={classes.newBuildingMetrics}>
              <BuildingStoreyCountInput variant="h4" />
              <BuildingGfaInput variant="h4" />
            </div>
            <div className={classes.newBuildingFootprints}>
              <BuildingFootprintTemplateList />
            </div>
          </div>
        )}
        {showSingleBuildingView && (
          <>
            <div className={classes.buildingsHeader}>
              {buildings.length === 1 ? (
                <div className={classes.newBuildingRow}>
                  <GhostButton
                    startIcon={<Icon category="mui" icon="Add" size="small" />}
                    onClick={handleAddBuilding}
                    disabled={!canAddNewBuilding}
                  >
                    New building
                  </GhostButton>
                </div>
              ) : (
                <div className={classes.selectedBuildingTitle}>
                  <Tooltip title="Back">
                    <IconButton
                      category="mui"
                      icon="KeyboardArrowLeft"
                      shape="square"
                      onClick={deselectBuilding}
                      aria-label="Show all buildings"
                    />
                  </Tooltip>
                  <BuildingNameInput
                    label="Building name"
                    showLabel={false}
                    value={selectedBuildingName}
                    variant="h4"
                    editing={isEditingBuildingName}
                    selectOnFocus
                    onDoubleClick={handleEditBuildingName}
                    onCancel={handleCancelBuildingNameEdit}
                    onSave={handleSaveBuildingName}
                  />
                </div>
              )}
              <div className={classes.gfaColumn}>
                <BuildingGfaInput variant="h4" />
              </div>
            </div>
            <div className={classes.storeyCount}>
              <Typography variant="subtitle3">
                {`${totalStoreys} ${totalStoreys === 1 ? 'Storey' : 'Storeys'}`}
              </Typography>
              <Tooltip title={addStoreyTooltip}>
                <span>
                  <GhostButton
                    startIcon={<Icon category="mui" icon="Add" size="small" />}
                    onClick={addStorey}
                    disabled={!canAddStorey}
                  >
                    Add storey
                  </GhostButton>
                </span>
              </Tooltip>
            </div>
            <div className={classes.root}>
              <ul className={classes.list}>
                {orderedStoreys.map((storey) => {
                  const isSelected =
                    !isBuildingOutlineSelected &&
                    storey.id === selectedStorey?.id;
                  const isSettingsOpen =
                    isSelected && collapsedSettings[storey.id] === false;
                  return (
                    <React.Fragment key={storey.id}>
                      <StoreyRow
                        storey={storey}
                        storeyGfa={storeyGfaRecord[storey.id] ?? 0}
                        maxStoreyGfa={maxStoreyGfa}
                        isSelected={isSelected}
                        isSettingsOpen={isSettingsOpen}
                        onSelect={handleSelectStorey}
                        onToggleSettings={handleToggleStoreySettings}
                      />
                    </React.Fragment>
                  );
                })}
              </ul>
            </div>
            <AddActivityRefMenu />
          </>
        )}
      </SidePanel.Content.Section>
    </AddActivityRefMenuProvider>
  );
};
